import { FormEventHandler, useEffect, useRef, useState } from 'react';
import { appInsights } from '../appInsights';
import { PageButton } from '../components/PageButton';
import { PasswordInput } from '../components/PasswordInput';
import TillrAccountsModel from '../TillrAccountsModel';
import { Domain } from '../types';
import {
  clearRememberMeData,
  getApiBaseUrl,
  getErrorMessage,
  getRememberMeData,
  setRememberMeData,
  validateEmail,
} from '../utils';
import {
  NO_ACTIVE_TENANTS_ERROR_CODE,
  NoActiveTenantsErrorMessage,
} from './NoActiveTenantsErrorMessage';

function getVerifyLoginCodeErrorMessage(errorMessage: string): {
  message: string;
  disableLogin?: boolean;
} {
  switch (errorMessage) {
    case 'NotLatest': {
      return {
        message:
          'This verification code is not the latest that was sent, please check your email and try again.',
      };
    }
    case 'Expired': {
      return {
        message: 'This verification code has expired, please refresh to log in again.',
        disableLogin: true,
      };
    }
    case 'Invalid': {
      return {
        message: 'Invalid verification code, please check your email and try again.',
      };
    }
    case 'Locked': {
      return {
        message: 'This account has now been locked. Please contact support@tillr.io.',
        disableLogin: true,
      };
    }
    case 'Success': {
      // Shouldn't happen
      throw new Error('Error: VerifyLoginCode is Success');
    }
    default: {
      appInsights.trackException({
        exception: new Error(`Unexpected VerifyLoginCodeErrorMessage: ${errorMessage}.`),
      });
      return { message: errorMessage };
    }
  }
}

export function LogInPage() {
  const formRef = useRef<HTMLFormElement>(null);

  const rememberMeData = getRememberMeData();

  const [email, setEmail] = useState(rememberMeData ?? '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(Boolean(rememberMeData));

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [domains, setDomains] = useState<Array<Domain>>();
  const [requiresVerification, setRequiresVerification] = useState<boolean>();
  const [loginCode, setLoginCode] = useState<string>();
  const [loginCodeVerified, setLoginCodeVerified] = useState<boolean>();
  const [tenantId, setTenantId] = useState<number>();
  const [loginDisabled, setLoginDisabled] = useState<boolean>();

  const handleGetUserDomains: FormEventHandler = (e) => {
    e.preventDefault();

    if (!validateEmail(email) || !password) {
      setError('Error - Invalid or empty email address or password.');
      return;
    }

    setError('');
    setSubmitting(true);

    if (rememberMe) {
      setRememberMeData(email);
    } else {
      clearRememberMeData();
    }

    fetch(`${getApiBaseUrl()}/GetUserDomains`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      body: JSON.stringify({
        Email: email,
        Password: password,
        ClientId: TillrAccountsModel.clientId,
      }),
    })
      .then((response) => {
        if (response.ok) {
          response.json().then((domains: Array<Domain>) => {
            if (!domains.length) {
              setError(NO_ACTIVE_TENANTS_ERROR_CODE);
            } else if (domains.length === 1 || document.referrer.split('.')[0].match('hq')) {
              const domain = domains[0];
              // Important this is set first
              if (domain.RequiresVerification) {
                setRequiresVerification(true);
              }
              setTenantId(domain.TenantId);
            } else {
              setDomains(domains);
            }
          });
        } else {
          getErrorMessage(response).then(({ errorMessage, showUser }) => {
            if (showUser) {
              setError(errorMessage);
            } else {
              appInsights.trackException({ exception: new Error(errorMessage) });
              setError("There's been an error. Please try again later.");
            }
          });
        }
      })
      .finally(() => setSubmitting(false));
  };

  const handleVerifyLogin: FormEventHandler = (e) => {
    e.preventDefault();

    if (!validateEmail(email) || !password) {
      setError('Error - Invalid or empty email address or password.');
      return;
    }

    setError('');
    setSubmitting(true);

    fetch(`${getApiBaseUrl()}/VerifyLoginCode`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      body: JSON.stringify({
        Email: email,
        Password: password,
        TenantId: tenantId,
        Code: loginCode,
      }),
    })
      .then((response) => {
        if (response.ok) {
          setLoginCodeVerified(true);
        } else {
          getErrorMessage(response).then(({ errorMessage, showUser }) => {
            if (showUser) {
              const { message, disableLogin } = getVerifyLoginCodeErrorMessage(errorMessage);
              setError(message);
              if (disableLogin) {
                setLoginDisabled(true);
              }
            } else {
              appInsights.trackException({ exception: new Error(errorMessage) });
              setError("There's been an error. Please try again later.");
            }
          });
        }
      })
      .finally(() => setSubmitting(false));
  };

  const handleSubmit: FormEventHandler = (e) => {
    if (!tenantId) {
      handleGetUserDomains(e);
    }
    if (requiresVerification) {
      handleVerifyLogin(e);
    }
    // Otherwise allow to post the form
  };

  useEffect(() => {
    if ((!requiresVerification || loginCodeVerified) && tenantId && formRef.current) {
      formRef.current.submit();
      setSubmitting(true);
    }
  }, [requiresVerification, loginCodeVerified, tenantId]);

  return (
    <form
      action={TillrAccountsModel.viewModel?.loginUrl}
      method="POST"
      noValidate
      ref={formRef}
      className={submitting ? 'pending' : ''}
      onSubmit={handleSubmit}
    >
      {/* hidden so the form is still submittable from the DOM */}
      <section id="login-wrapper" className={domains || requiresVerification ? 'hidden' : ''}>
        <div className="progress"></div>
        <h1 id="title">Tillr Accounts</h1>
        {TillrAccountsModel.viewModel?.antiForgery && (
          <input
            type="hidden"
            name={TillrAccountsModel.viewModel.antiForgery.name}
            value={TillrAccountsModel.viewModel.antiForgery.value}
          />
        )}
        <input type="hidden" name="clientId" value={TillrAccountsModel.clientId} />
        <input type="hidden" name="username" value={`${tenantId}|${email}|${loginCode}`} />
        {error && (
          <div className="message error">
            {error === NO_ACTIVE_TENANTS_ERROR_CODE ? (
              <NoActiveTenantsErrorMessage />
            ) : (
              <p dangerouslySetInnerHTML={{ __html: error }} />
            )}
          </div>
        )}

        <fieldset>
          <div className="form-group">
            <label htmlFor="email">Email address</label>
            <input
              type="text"
              id="email"
              value={email}
              required
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput id="password" required onChange={setPassword} />
          </div>
          <div className="form-group">
            <div className="checkbox">
              <label htmlFor="remember">
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                  checked={rememberMe}
                  className="magic-checkbox"
                  onChange={(e) => setRememberMe(e.currentTarget.checked)}
                />
                <label htmlFor="remember"></label>
                Remember me?
              </label>
            </div>
          </div>
        </fieldset>
        <div className="form-group">
          <button type="submit" className="primary" disabled={submitting}>
            Sign in
          </button>
        </div>
        <div className="form-group" style={{ marginTop: '20px' }}>
          <PageButton to="forgottenpassword">Forgotten your password?</PageButton>
        </div>
      </section>

      {domains && (
        <section>
          <div className="progress"></div>
          <h1 id="title">Tillr Accounts</h1>
          <h2>Choose an account</h2>
          <ol>
            {domains
              .sort((a, b) => a.Name.localeCompare(b.Name))
              .map(({ TenantId, Name }) => (
                <li key={TenantId}>
                  <button type="button" onClick={() => setTenantId(TenantId)}>
                    <span>{Name}</span> <i></i>
                  </button>
                </li>
              ))}
          </ol>
        </section>
      )}

      {requiresVerification && (
        <section>
          <div className="progress"></div>
          <h1 id="title">Tillr Accounts</h1>
          <div className="message">
            <p>
              For your security, we've sent a one-time verification code to <b>{email}</b>.
            </p>
            <p>Enter the 6-digit code to verify your identity and continue.</p>
            <p>This code will expire in 15 minutes.</p>
            <p>
              <small>Didn’t get it? Check your spam or junk folder.</small>
            </p>
          </div>

          {error && (
            <div className="message error">
              <p dangerouslySetInnerHTML={{ __html: error }} />
            </div>
          )}

          <fieldset>
            <div className="form-group">
              <label htmlFor="loginCode">Verification code</label>
              <input
                type="text"
                id="loginCode"
                required
                onChange={(e) => setLoginCode(e.currentTarget.value)}
              />
            </div>
          </fieldset>

          <button
            type="button"
            className="primary"
            disabled={submitting || loginDisabled}
            onClick={handleVerifyLogin}
          >
            Sign in
          </button>
        </section>
      )}
    </form>
  );
}
